import { useEffect, useState } from 'react';
import clsx from 'clsx';
import PageHeader from '@/components/common/PageHeader.jsx';
import StickyActions from '@/components/StickyActions.jsx';
import { useOperatorCompactViewport } from '@/utils/operatorCompact.js';

const COMPACT_HEIGHT = 700;

export function useCargasCompact() {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerHeight < COMPACT_HEIGHT;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncCompact = () => {
      setIsCompact(window.innerHeight < COMPACT_HEIGHT);
    };

    syncCompact();
    window.addEventListener('resize', syncCompact);
    return () => window.removeEventListener('resize', syncCompact);
  }, []);

  return isCompact;
}

export function useOperatorCompact() {
  const { isCompactHeight, isCompactWidth, isOperatorCompactViewport } = useOperatorCompactViewport();
  return {
    isCompactHeight,
    isSmallWidth: isCompactWidth,
    isOperatorCompact: isOperatorCompactViewport,
  };
}

export function useSaveShortcut(handler, enabled = true) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const onKeyDown = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handler();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, handler]);
}

export const cargasPanelClassName =
  'rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] shadow-[0_10px_24px_rgba(var(--shadow),0.1)]';

export const cargasInputClassName =
  'w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.52)] px-3 py-2 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]';

export const cargasButtonClassName =
  'inline-flex min-h-9 items-center justify-center rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] px-3 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--bg),0.48)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]';

export const cargasPrimaryButtonClassName =
  'inline-flex min-h-9 items-center justify-center rounded-xl bg-[rgb(var(--brand))] px-3 py-2 text-sm font-semibold text-[rgb(var(--bg))] transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60';

export function CargasPage({
  title,
  description,
  breadcrumbs = [],
  actions,
  footer,
  hideHeaderOnCompact = false,
  compactOverride = false,
  children,
  className,
}) {
  const isCompact = useCargasCompact();
  const { isOperatorCompact } = useOperatorCompact();
  const shouldCompact = compactOverride || isCompact || isOperatorCompact;
  const showHeader = !(hideHeaderOnCompact && shouldCompact);

  return (
    <div className={clsx('flex h-full min-h-0 flex-col overflow-hidden', shouldCompact ? 'gap-2' : 'gap-3', className)}>
      {showHeader ? (
        <PageHeader
          title={title}
          description={shouldCompact ? undefined : description}
          breadcrumbs={breadcrumbs}
          actions={actions}
        />
      ) : null}
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
      {footer ? <div className="shrink-0">{footer}</div> : null}
    </div>
  );
}

export function CargasViewport({ children, className }) {
  return (
    <div className={clsx('flex h-full min-h-0 flex-col overflow-hidden', className)}>
      {children}
    </div>
  );
}

export function CargasSection({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
  headerClassName,
  compact = false,
  density = 'default',
}) {
  const isTight = density === 'tight';

  return (
    <section className={clsx(cargasPanelClassName, 'flex min-h-0 min-w-0 flex-col overflow-hidden', className)}>
      {title || description || actions ? (
        <div
          className={clsx(
            'flex flex-wrap items-start justify-between gap-2 border-b border-[rgba(var(--border),0.4)] px-4',
            isTight ? 'py-1.5' : compact ? 'py-2' : 'py-3',
            headerClassName,
          )}
        >
          <div className="min-w-0">
            {title ? <h2 className={clsx('font-semibold text-[rgb(var(--fg))]', isTight ? 'text-[13px]' : 'text-sm')}>{title}</h2> : null}
            {description ? (
              <p className={clsx('text-[rgba(var(--muted),0.92)]', isTight ? 'mt-0.5 text-[11px]' : 'mt-1 text-xs')}>{description}</p>
            ) : null}
          </div>
          {actions ? <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={clsx('min-h-0 min-w-0 flex-1', isTight ? 'p-2.5' : compact ? 'p-3' : 'p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export function CargasTableShell({ children, className }) {
  return (
    <div className={clsx('min-h-0 overflow-auto rounded-xl border border-[rgba(var(--border),0.45)]', className)}>
      {children}
    </div>
  );
}

export function CargasStickyActions({ summary, actions }) {
  return <StickyActions left={summary} right={actions} />;
}
